package com.evoapps.evoshift.domain.budget

import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.repository.LabourInclusionRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.serialization.Serializable

/*
 * SH-206: whether a person's shifts count in the labour figures: the labour cost, labour % and
 * SPMH, hours AND cost together, planned and actual (daily records, the month's totals, the
 * dashboards, the financial summaries, the daily result, the exports, the open shifts' average
 * cost per hour). The owner who works behind the counter without a wage makes SPMH look worse and
 * labour % better when they count; some owners want that, others not. One switch per person and
 * workplace ("Count in labour cost and SPMH"), set by the owner or a financial manager.
 *
 * The person's own hours, pay estimate, calendar and conflicts are not affected: the switch only
 * decides what the workplace's labour figures hold.
 *
 * Default (no setting, also all data from before): on for everyone, except people who work here
 * without a wage: the owner and co-owners (OWNER permission) without pay terms, and the people
 * whose shifts are not paid work here (WorkplacePayrollSettings.ownerShiftsUnpaid,
 * unpaidUserIds). That is what the figures held before the switch existed: those people's shifts
 * had no cost snapshot, so neither their cost nor their hours counted.
 *
 * Set ON for someone working without a wage, their hours count at no cost. Set OFF for someone
 * with pay terms, their shifts keep their cost snapshots (their pay estimate) but stay out of the
 * workplace's figures. Missing-pay warnings never name a person who does not count.
 *
 * History: a change applies from the month it is made in (the whole month, so a month's labour %
 * and SPMH always describe the same people); earlier months keep what they had, as the cost
 * rules' snapshots do. The same rule is in functions/labourInclusion.js; both are tested against
 * firebase/tests/golden/labour-inclusion/cases.json.
 */

/** SH-206: from [fromMonth] ("YYYY-MM") on, [counts] says whether the person counts. */
@Serializable
data class LabourInclusionPeriod(val fromMonth: String, val counts: Boolean)

/** SH-206: what the default rests on, for one person at one workplace. */
data class LabourInclusionFacts(
    /** They have pay terms at the workplace (users/{uid}/sensitive/{workplaceId}). */
    val hasPayTerms: Boolean,
    /** The workplace's owner, or a co-owner (OWNER permission there). */
    val isOwnerLevel: Boolean,
    /** Their shifts are paid work here ([Workplace.isPaid]). */
    val isPaidWork: Boolean = true
) {
    /** They work here without a wage: when they count, their hours count at no cost. */
    val worksWithoutWage: Boolean get() = !isPaidWork || (isOwnerLevel && !hasPayTerms)
}

object LabourInclusion {
    /** Whether someone counts without a setting: everyone but those working here without a wage. */
    fun byDefault(facts: LabourInclusionFacts): Boolean = !facts.worksWithoutWage

    /** The setting that applies in [month] ("YYYY-MM"): the latest period from that month or before; null: none. */
    fun settingIn(periods: List<LabourInclusionPeriod>, month: String): Boolean? =
        periods.filter { it.fromMonth <= month }.maxByOrNull { it.fromMonth }?.counts

    /** THE rule: whether the person counts in [month]'s labour figures. */
    fun counts(periods: List<LabourInclusionPeriod>, month: String, facts: LabourInclusionFacts): Boolean =
        settingIn(periods, month) ?: byDefault(facts)

    /**
     * [userId]'s facts at [workplace]: [permission] there and their pay terms ([financial], null:
     * none). SH-232: in [month] ("YYYY-MM") their work is paid when at least one of its days is
     * (their unpaid days then count their hours at no cost, Workplace.isPaid by day); without a
     * month, today's list.
     */
    fun factsOf(userId: String, workplace: Workplace, permission: UserPermission?, financial: FinancialEngagement?, month: String? = null): LabourInclusionFacts =
        LabourInclusionFacts(
            hasPayTerms = financial != null,
            isOwnerLevel = userId == workplace.ownerId || permission == UserPermission.OWNER,
            isPaidWork = month?.let { workplace.isPaidIn(userId, it) } ?: workplace.isPaid(userId)
        )

    /** Whether [userId] counts in [month] at [workplace] (see [counts]). */
    fun counts(
        userId: String,
        workplace: Workplace,
        month: String,
        permission: UserPermission?,
        financial: FinancialEngagement?,
        periods: List<LabourInclusionPeriod>
    ): Boolean = counts(periods, month, factsOf(userId, workplace, permission, financial, month))

    /** "YYYY-MM" of [year] and [month] (1-12). */
    fun monthOf(year: Int, month: Int): String = "$year-${month.toString().padStart(2, '0')}"

    /** "YYYY-MM" of [date]. */
    fun monthOf(date: LocalDate): String = monthOf(date.year, date.month.number)

    /**
     * [periods] with [counts] from [fromMonth] on: periods starting that month or later are
     * replaced (the new setting is the latest), earlier ones stay (their months keep them). A
     * setting the same as what already applies changes nothing. At most [MAX_PERIODS] are kept.
     */
    fun withSetting(periods: List<LabourInclusionPeriod>, counts: Boolean, fromMonth: String, facts: LabourInclusionFacts): List<LabourInclusionPeriod> {
        val kept = periods.filter { it.fromMonth < fromMonth }.sortedBy { it.fromMonth }
        val before = settingIn(kept, fromMonth) ?: byDefault(facts)
        if (before == counts) return kept
        return (kept + LabourInclusionPeriod(fromMonth, counts)).takeLast(MAX_PERIODS)
    }

    const val MAX_PERIODS = 24
}

/**
 * SH-206: who counts in one month's labour figures at a workplace. [UNKNOWN] when the settings
 * could not be read (a caller without VIEW_FINANCES, tests without a repository): nobody is
 * excluded beyond what the figures did before (people whose shifts are not paid work).
 */
data class LabourPeople(
    /** People whose shifts stay out of the month's labour cost, labour % and SPMH. */
    val excluded: Set<String> = emptySet(),
    /** People who count but work without a wage: their hours count at no cost. */
    val countedWithoutWage: Set<String> = emptySet(),
    /** The people the month's figures exclude, from settings that could be read. */
    val isKnown: Boolean = false
) {
    fun counts(userId: String): Boolean = userId !in excluded

    companion object {
        val UNKNOWN = LabourPeople()
    }
}

/**
 * SH-206: reads who counts in a month: the settings (workplaces/{id}/labour_inclusion, one query),
 * the people's permission (their profiles) and, for the owner-level ones, whether they have pay
 * terms. Money: only callers with VIEW_FINANCES (who may read all of it) should ask.
 */
class LabourInclusionReader(
    private val repository: LabourInclusionRepository,
    private val userRepository: UserRepository
) {
    /**
     * Who of [userIds] counts in [month] at [workplace]. [knownTerms]: pay terms already read (by
     * user id), not read again. Fails when the settings cannot be read (the caller falls back on
     * [LabourPeople.UNKNOWN]).
     */
    suspend fun people(
        workplace: Workplace,
        userIds: Collection<String>,
        month: String,
        knownTerms: Map<String, FinancialEngagement> = emptyMap()
    ): Result<LabourPeople> = runCatching {
        val ids = userIds.distinct()
        if (ids.isEmpty()) return@runCatching LabourPeople(isKnown = true)
        coroutineScope {
            val settings = async { repository.getAll(workplace.id).getOrThrow() }
            val team = async { userRepository.getAssignableEmployees(workplace.id).getOrDefault(emptyList()).associateBy { it.uid } }
            val profiles = team.await()
            val permissions = ids.map { uid ->
                async { uid to (profiles[uid] ?: profileOf(uid))?.engagements?.get(workplace.id)?.permission }
            }.awaitAll().toMap()
            // Pay terms decide only the owner-level people's default.
            val ownerLevel = ids.filter { it == workplace.ownerId || permissions[it] == UserPermission.OWNER }
            val terms = ownerLevel.map { uid ->
                async {
                    uid to (knownTerms[uid] ?: userRepository.getSensitiveUserData(uid).getOrNull()?.financialEngagements?.get(workplace.id))
                }
            }.awaitAll().toMap()
            val periods = settings.await()
            val excluded = mutableSetOf<String>()
            val withoutWage = mutableSetOf<String>()
            ids.forEach { uid ->
                val facts = LabourInclusion.factsOf(uid, workplace, permissions[uid], terms[uid] ?: knownTerms[uid], month)
                if (!LabourInclusion.counts(periods[uid].orEmpty(), month, facts)) excluded += uid
                else if (facts.worksWithoutWage) withoutWage += uid
            }
            LabourPeople(excluded, withoutWage, isKnown = true)
        }
    }

    private suspend fun profileOf(uid: String): User? = userRepository.getUserProfile(uid).getOrNull()
}
